import * as KendoChart from '@progress/kendo-react-charts'
import { DateRangePicker } from '@progress/kendo-react-dateinputs'
import { ExcelExport } from '@progress/kendo-react-excel-export'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import bodybuilder from 'bodybuilder'
import { LoadSegmentCodes } from 'components/common/load-partner-codes/component'
import { DropdownComponent } from 'components/dropdown/component'
import { HeaderComponent } from 'components/header/component'
import { appendTimezoneIfAvailable, DateRangesAsDropdownOptions, getMessageSendDateFilterField, SuspectFilterDropdownOptions
} from 'elasticsearch/constants'
import { AggregationType, DateRanges, FilterType, IndexName, toFilter } from 'elasticsearch/constants'
import { ResponseAggregate, ResponsePayload } from 'elasticsearch/types'
import {
  ElasticsearchComponent,
  ElasticsearchProps,
} from 'generic/elasticSearchComponent'
import * as GenericRedux from 'generic/genericRedux'
import * as React from 'react'
import { Button } from 'reactstrap'
import { DEFAULT_COMMUNITY_PARTNER_CODES } from 'shared_server_client/constants'
import * as Constants from './constants'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'
import { ConnectedComponentClass } from "react-redux"
import {Fields} from "../../shared/modals"
import * as Utils from './utils'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { ComponentType } from 'react'
import { PDFReportHeader } from './PDFReportHeader'
import { Loading } from 'components/loading'

const initialState = {
  selectedDateRange: DateRangesAsDropdownOptions[4], // Index 4 = "Last ~30 Days" (PastMonth)
  selectedSegmentCode: DEFAULT_COMMUNITY_PARTNER_CODES.ALL,
}

export class ActiveSubscribersComponent extends RasaAnalyticsComponent<any, any> {
  constructor(props) {
    super(props, initialState)
    // Override parent's default date range to use 30 days
    this.state = {
      ...this.state,
      selectedDateRange: DateRangesAsDropdownOptions[4] // "Last ~30 Days"
    }
  }

  public componentDidMount() {
    super.componentDidMount()
    this.minCustomDateRange()
  }

  public render() {
    return(
      <div className="analytics-component">
        <HeaderComponent
          title={'ANALYTICS'}
          subTitle={'Active Subscribers'}
          description={[
            'Track the number of unique subscribers who engage with your content over time.',
            'Shows trends in subscriber activity and engagement across different time periods.',
            'Helps measure newsletter growth, audience retention, and engagement patterns.',
          ]}
        />
        <div className="dropdown-header">
          <div className="dropdown-inline">
            <DropdownComponent data={DateRangesAsDropdownOptions}
                                selected={this.state.selectedDateRange.key}
                                onChange={this.dateChanged}/>
          </div>
          <div className="dropdown-inline">
            <DropdownComponent data={SuspectFilterDropdownOptions}
                               selected={this.state.selectedSuspectClick.key}
                               onChange={this.suspectedClickChanged}/>
          </div>
        </div>
        <div className="dropdown-segment-code">
        <LoadSegmentCodes segmentCode={this.state.selectedSegmentCode} setSegmentCode={this.segmentCodeChanged}
          hideLabel={true}></LoadSegmentCodes>
        </div>
        <div className="date-range-picker">
        {this.state.selectedDateRange.key === '7' ?
        <DateRangePicker min={this.state.minCustomDateRange}
                          max ={new Date()}
                          onChange={this.createCustomDate} /> : null}
        </div>
        {this.state.isFilterLoaded &&
        <AnalyticsActiveSubscribersComponent
          dateRange={this.state.selectedDateRange.value}
          dateRangeLabel={this.state.selectedDateRange.description}
          timezone={this.state.timezone}
          useTimezoneForAnalytics={this.state.useTimezoneForAnalytics}
          selectedSegmentCode={this.state.selectedSegmentCode}
          suspectClick={this.state.selectedSuspectClick.value}
        />}
      </div>
    )
  }
}

interface DailyData {
  date: string,
  activeSubscribers: number,
  totalEngagements: number,
  avgEngagementPerSubscriber: number,
  dateLabel: string,
}

interface ActiveSubscribersData {
  dailyData: DailyData[],
  totalActiveSubscribers: number,
  avgDailyActiveSubscribers: number,
  minDailyActiveSubscribers: number,
  maxDailyActiveSubscribers: number,
  growthRate: number,
  peakDay: string,
}

interface ActiveSubscribersProps extends ElasticsearchProps<ActiveSubscribersData> {
  dateRange: string,
  selectedSegmentCode: string,
  suspectClick?: any
  timezone?: string,
  useTimezoneForAnalytics?: any,
  dateRangeLabel?: string,
}

interface ActiveSubscribersState {
  loaded: boolean,
  isLoadingData: boolean,
}

const DATE_AGGREGATION: string = 'date'
const UNIQUE_SUBSCRIBERS_AGGREGATION: string = 'unique_subscribers'

class ActiveSubscribersClass extends ElasticsearchComponent<ActiveSubscribersData, ActiveSubscribersProps, ActiveSubscribersState> {
  private xlsxExport: any = null
  private pdfExportRef: React.RefObject<HTMLDivElement> = React.createRef()

  constructor(p: ActiveSubscribersProps) {
    super(p, IndexName.EVENTS)
    this.state = {
      loaded: false,
      isLoadingData: false,
    }
    this.reportName = Constants.REPORT_NAMES.ACTIVE_SUBSCRIBERS || 'Active Subscribers'
  }

  private formatDateLabel(dateKey: string): string {
    const date = new Date(dateKey)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  public parseResponse(payload: ResponsePayload): Promise<ActiveSubscribersData> {
    this.setState({ loaded: true, isLoadingData: false })

    const dateAggregations = payload.aggregations[DATE_AGGREGATION].buckets

    const dailyData: DailyData[] = dateAggregations
      .filter((aggregation: ResponseAggregate) => {
        // Only include days with actual engagement (totalEngagements > 0)
        const totalEngagements = aggregation.doc_count || 0
        return totalEngagements > 0
      })
      .map((aggregation: ResponseAggregate) => {
        const dateKey = aggregation.key_as_string || aggregation.key.toString()
        const dateLabel = this.formatDateLabel(dateKey)

        const uniqueSubscribers = aggregation[UNIQUE_SUBSCRIBERS_AGGREGATION]
        const activeSubscribers = uniqueSubscribers ? uniqueSubscribers.value : 0

        const totalEngagements = aggregation.doc_count || 0
        const avgEngagementPerSubscriber = activeSubscribers > 0 ? totalEngagements / activeSubscribers : 0

        return {
          date: dateKey,
          dateLabel,
          activeSubscribers: Math.round(activeSubscribers),
          totalEngagements,
          avgEngagementPerSubscriber: Math.round(avgEngagementPerSubscriber * 100) / 100,
        }
      })
      .sort((a, b) => {
        const aDate = new Date(a.date)
        const bDate = new Date(b.date)
        return aDate.getTime() - bDate.getTime()
      })

    const totalActiveSubscribers = dailyData.reduce((sum, data) => sum + data.activeSubscribers, 0)
    const avgDailyActiveSubscribers = dailyData.length > 0 ? Math.round(totalActiveSubscribers / dailyData.length) : 0

    // Calculate min and max daily active subscribers
    const minDailyActiveSubscribers = dailyData.length > 0
      ? Math.min(...dailyData.map(data => data.activeSubscribers))
      : 0
    const maxDailyActiveSubscribers = dailyData.length > 0
      ? Math.max(...dailyData.map(data => data.activeSubscribers))
      : 0

    let growthRate = 0
    if (dailyData.length >= 2) {
      const firstDay = dailyData[0].activeSubscribers
      const lastDay = dailyData[dailyData.length - 1].activeSubscribers
      if (firstDay > 0) {
        growthRate = Math.round(((lastDay - firstDay) / firstDay) * 10000) / 100
      }
    }

    const peakDayData = dailyData.reduce((prev, current) =>
      (prev.activeSubscribers > current.activeSubscribers) ? prev : current,
      dailyData[0] || {
        date: '',
        dateLabel: 'N/A',
        activeSubscribers: 0,
        totalEngagements: 0,
        avgEngagementPerSubscriber: 0,
      }
    )

    return Promise.resolve({
      dailyData,
      totalActiveSubscribers,
      avgDailyActiveSubscribers,
      minDailyActiveSubscribers,
      maxDailyActiveSubscribers,
      growthRate,
      peakDay: peakDayData && peakDayData.dateLabel ? peakDayData.dateLabel : 'N/A',
    })
  }

  public componentDidUpdate(oldProps: ActiveSubscribersProps) {
    if (this.props.dateRange !== oldProps.dateRange ||
        this.props.suspectClick !== oldProps.suspectClick ||
        this.props.selectedSegmentCode !== oldProps.selectedSegmentCode) {
      this.setState({ isLoadingData: true })
      this.search()
    }
  }

  public searchPayload(): any {
    const search = bodybuilder().size(0)
       .filter(FilterType.range, getMessageSendDateFilterField(this.props.dateRange, this.props.useTimezoneForAnalytics), toFilter(this.props.dateRange || DateRanges.PastMonth, this.props.timezone, this.props.useTimezoneForAnalytics))

    if (this.props.selectedSegmentCode === DEFAULT_COMMUNITY_PARTNER_CODES.NO_CODE) {
      search.notFilter(FilterType.exists, 'community_partner_code.keyword')
    } else if (this.props.selectedSegmentCode !== DEFAULT_COMMUNITY_PARTNER_CODES.ALL) {
      search.filter(FilterType.term, 'community_partner_code.keyword', this.props.selectedSegmentCode)
    }

    if (this.props.suspectClick && Utils.isRealClickSelected(this.props.suspectClick)) {
      search.addFilter(FilterType.term, 'suspect_click', 0)
    }

    search.filter(FilterType.terms, 'event_name.keyword', ['click'])

    const dateHistogramConfig = {
      type: AggregationType.date_histogram,
      field: getMessageSendDateFilterField(this.props.dateRange, this.props.useTimezoneForAnalytics),
      extra: appendTimezoneIfAvailable({
        interval: 'day',
        min_doc_count: 1  // Only include days with at least 1 engagement
      }, this.props.dateRange, this.props.timezone, this.props.useTimezoneForAnalytics),
      name: DATE_AGGREGATION,
    }

    // Add the date histogram aggregation
    const searchWithDateAgg = this.addAggregation(search, dateHistogramConfig as any)

    // Manually add both child aggregations to the date histogram
    const finalQuery = searchWithDateAgg.build()

    // Add cardinality aggregation to the date histogram
    if (finalQuery.aggs && finalQuery.aggs[DATE_AGGREGATION]) {
      finalQuery.aggs[DATE_AGGREGATION].aggs = {
        [UNIQUE_SUBSCRIBERS_AGGREGATION]: {
          cardinality: {
            field: 'community_person_id'
          }
        }
      }
    }

    return finalQuery
  }

  public exportToPDF = async () => {
    if (!this.pdfExportRef.current) return

    try {
      const element = this.pdfExportRef.current
      element.classList.add('pdf-export-mode')
      await new Promise(resolve => setTimeout(resolve, 100))

      const canvas = await html2canvas(element, {
        height: element.scrollHeight,
        width: element.scrollWidth,
        useCORS: true,
        scale: 2
      })

      element.classList.remove('pdf-export-mode')

      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF('l', 'mm', 'a4')

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = 10

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
      pdf.save('active-subscribers-report.pdf')
    } catch (error) {
      if (this.pdfExportRef.current) {
        this.pdfExportRef.current.classList.remove('pdf-export-mode')
      }
    }
  }

  public render = () => <div className="analytics">
    <div className="active-subscribers" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
      <h4>Active Subscribers Overview</h4>
      {this.props.results && (
        <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
          <div>
            <strong>Average Active:</strong> {this.props.results.avgDailyActiveSubscribers.toLocaleString()}
          </div>
          <div>
            <strong>Min Active:</strong> {this.props.results.minDailyActiveSubscribers.toLocaleString()}
          </div>
          <div>
            <strong>Max Active:</strong> {this.props.results.maxDailyActiveSubscribers.toLocaleString()}
          </div>
          <div>
            <strong>Growth Rate:</strong> {this.props.results.growthRate > 0 ? '+' : ''}{this.props.results.growthRate}%
          </div>
          <div>
            <strong>Peak Day:</strong> {this.props.results.peakDay}
          </div>
        </div>
      )}
    </div>

    <div className="active-subscribers-chart">
      {this.state.isLoadingData ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loading size="32" />
        </div>
      ) : (this.props.results && this.props.results.dailyData.length > 0 ?
        <div>
          <div ref={this.pdfExportRef} className="pdf-report-container">
            <PDFReportHeader
              title="Active Subscribers Report"
              dateRange={this.props.dateRange}
              dateRangeLabel={this.props.dateRangeLabel}
              fields={[
                {
                  label: 'Segment',
                  value: this.props.selectedSegmentCode === 'ALL' ? 'All Segments' : this.props.selectedSegmentCode
                },
                {
                  label: 'Average Active',
                  value: this.props.results.avgDailyActiveSubscribers.toLocaleString()
                },
                {
                  label: 'Min Active',
                  value: this.props.results.minDailyActiveSubscribers.toLocaleString()
                },
                {
                  label: 'Max Active',
                  value: this.props.results.maxDailyActiveSubscribers.toLocaleString()
                },
                {
                  label: 'Growth Rate',
                  value: `${this.props.results.growthRate > 0 ? '+' : ''}${this.props.results.growthRate}%`
                }
              ]}
            />

            <div className="pdf-chart-section">
              <h3>Daily Active Subscribers Trend</h3>
              <KendoChart.Chart transitions={false} pannable={false} zoomable={false}>
                <KendoChart.ChartLegend position="bottom" orientation="horizontal"/>
                <KendoChart.ChartTooltip
                  render={({point}: KendoChart.TooltipContext) =>
                    <div className="active-subscribers-tooltip">
                      <div>{point.dataItem.dateLabel}</div>
                      <div>Active Subscribers: {point.value.toLocaleString()}</div>
                      <div>Total Engagements: {point.dataItem.totalEngagements.toLocaleString()}</div>
                      <div>Avg Engagement/Subscriber: {point.dataItem.avgEngagementPerSubscriber}</div>
                    </div>
                  }
                />
                <KendoChart.ChartCategoryAxis>
                  <KendoChart.ChartCategoryAxisItem
                    categories={this.props.results.dailyData.map(item => item.dateLabel)}
                    labels={{
                      rotation: -45,
                      skip: this.props.results.dailyData.length > 15 ? Math.ceil(this.props.results.dailyData.length / 15) : 0,
                      step: this.props.results.dailyData.length > 20 ? Math.ceil(this.props.results.dailyData.length / 15) : 1
                    }}
                  />
                </KendoChart.ChartCategoryAxis>
                <KendoChart.ChartValueAxis>
                  <KendoChart.ChartValueAxisItem title={{ text: "Active Subscribers" }} />
                </KendoChart.ChartValueAxis>
                <KendoChart.ChartSeries>
                  <KendoChart.ChartSeriesItem
                    type="line"
                    field="activeSubscribers"
                    name="Active Subscribers"
                    data={this.props.results.dailyData}
                    color="#4ecdc4"
                    markers={{ visible: true }}
                  />
                </KendoChart.ChartSeries>
              </KendoChart.Chart>
            </div>

            <div className="pdf-chart-section">
              <h3>Daily Engagement per Subscriber</h3>
              <KendoChart.Chart transitions={false} pannable={false} zoomable={false}>
                <KendoChart.ChartLegend position="bottom" orientation="horizontal"/>
                <KendoChart.ChartTooltip
                  render={({point}: KendoChart.TooltipContext) =>
                    <div className="engagement-tooltip">
                      <div>{point.dataItem.dateLabel}</div>
                      <div>Avg Engagement: {point.value}</div>
                    </div>
                  }
                />
                <KendoChart.ChartCategoryAxis>
                  <KendoChart.ChartCategoryAxisItem
                    categories={this.props.results.dailyData.map(item => item.dateLabel)}
                    labels={{
                      rotation: -45,
                      skip: this.props.results.dailyData.length > 15 ? Math.ceil(this.props.results.dailyData.length / 15) : 0,
                      step: this.props.results.dailyData.length > 20 ? Math.ceil(this.props.results.dailyData.length / 15) : 1
                    }}
                  />
                </KendoChart.ChartCategoryAxis>
                <KendoChart.ChartValueAxis>
                  <KendoChart.ChartValueAxisItem title={{ text: "Avg Engagements per Subscriber" }} />
                </KendoChart.ChartValueAxis>
                <KendoChart.ChartSeries>
                  <KendoChart.ChartSeriesItem
                    type="column"
                    field="avgEngagementPerSubscriber"
                    name="Avg Engagement per Subscriber"
                    data={this.props.results.dailyData}
                    color="#004f6d"
                  />
                </KendoChart.ChartSeries>
              </KendoChart.Chart>
            </div>

            <div className="pdf-table-section">
              <h3>Detailed Daily Data</h3>
              <Grid data={this.props.results.dailyData} className="analytics-counts-grid pdf-table" scrollable="none">
                <GridColumn field="dateLabel" title="Date"/>
                <GridColumn field="activeSubscribers" title="Active Subscribers" cell={({dataItem}) => <td>{dataItem.activeSubscribers.toLocaleString()}</td>}/>
                <GridColumn field="totalEngagements" title="Total Engagements" cell={({dataItem}) => <td>{dataItem.totalEngagements.toLocaleString()}</td>}/>
                <GridColumn field="avgEngagementPerSubscriber" title="Avg Engagement/Subscriber"/>
              </Grid>
            </div>

          </div>

          <div className="active-subscribers-exports">
            <Button
              disabled={this.props.results.dailyData.length < 1 || this.state.isLoadingData}
              onClick={() => this.xlsxExport.save()}
              style={{ marginRight: '10px' }}>
              Export Excel
            </Button>
            <Button
              disabled={this.props.results.dailyData.length < 1 || this.state.isLoadingData}
              onClick={this.exportToPDF}
              color="secondary">
              Export PDF
            </Button>
            <ExcelExport data={this.props.results.dailyData}
              fileName="ActiveSubscribersReport.xlsx"
              ref={(exporter) => {this.xlsxExport = exporter}}>
              <Grid data={this.props.results.dailyData} className="analytics-counts-grid" scrollable="none">
                <GridColumn field="dateLabel" title="Date"/>
                <GridColumn field="activeSubscribers" title="Active Subscribers" cell={({dataItem}) => <td>{dataItem.activeSubscribers.toLocaleString()}</td>}/>
                <GridColumn field="totalEngagements" title="Total Engagements" cell={({dataItem}) => <td>{dataItem.totalEngagements.toLocaleString()}</td>}/>
                <GridColumn field="avgEngagementPerSubscriber" title="Avg Engagement/Subscriber"/>
              </Grid>
            </ExcelExport>
          </div>
        </div> :
        <div>
          <p className="no-data-tag">
            {Constants.NO_DATA_COPY}
          </p>
        </div>)
      }
    </div>
  </div>
}

export const AnalyticsActiveSubscribersComponent: ConnectedComponentClass<ComponentType<ActiveSubscribersClass>, Fields> = GenericRedux.registerNewComponent(
  ActiveSubscribersClass, 'analytics_active_subscribers', {})